import React from 'react';
import { ChatThread, ChatDayDivider, Spinner } from '@parsina/framework';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { withDayDividers, lastAssistantId } from '../../screens/Chat/Chat.shared.js';
import StudentMessage from './StudentMessage.jsx';
import AssistantMessage from './AssistantMessage.jsx';

// The message column. core.md §9.3, §9.6.
//
// The kit's `ChatThread` already carries the two behaviours this screen must not
// re-derive: `role="log" aria-live="polite"` on the column, and backwards paging
// as a BUTTON rather than a scroll trigger. «پیام‌های قدیمی‌تر» loads sixty more;
// nothing here ever fires a fetch from a scroll position.
//
// The day dividers are computed once in Chat.shared.js against IRAN days, and
// `AiAnswerNote` rides under the LATEST assistant message only — under every one
// it becomes wallpaper, which is the failure mode a standing notice has.

/** The block that stands where the answer will be, while the unary call runs. */
function TypingIndicator() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="typing-indicator"
      style={{
        alignSelf: 'stretch',
        display: 'flex', alignItems: 'center', gap: 10,
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '14px 16px',
        color: CT.mute,
        fontSize: 13,
      }}
    >
      {/* The Spinner carries its own `role="status"`, and its DEFAULT accessible
          name is a Persian literal — so the name is always passed here, or an
          English page announces its loading state in Persian. */}
      <Spinner size={16} thickness={2} label={t('chat.message.typing')} />
      {t('chat.composer.thinking')}
    </div>
  );
}

export default function MessageList({
  messages,
  reveal = null,
  confidence = null,
  sending = false,
  hasOlder = false,
  loadingOlder = false,
  onLoadOlder,
  onRetry,
  onRegenerate,
  fill = true,
}) {
  const { t, formatDate } = useTranslation();
  const rows = React.useMemo(() => withDayDividers(messages), [messages]);
  const latestAnswer = React.useMemo(() => lastAssistantId(messages), [messages]);

  return (
    <ChatThread
      fill={fill}
      padBlockRem={1}
      autoScroll
      hasOlder={hasOlder}
      onLoadOlder={onLoadOlder}
      loadingOlder={loadingOlder}
      loadOlderLabel={loadingOlder ? t('chat.message.loadingOlder') : t('chat.message.older')}
      data-testid="chat-thread"
    >
      {rows.map((row) => {
        if (row.kind === 'day') {
          return <ChatDayDivider key={row.key} label={formatDate(`${row.day}`, 'weekdayDayMonth')} />;
        }
        const message = row.message;
        if (message.role === 'assistant') {
          return (
            <AssistantMessage
              key={row.key}
              message={message}
              revealText={reveal && reveal.id === message.id ? reveal.text : null}
              confidence={message.id === latestAnswer ? confidence : null}
              showAnswerNote={message.id === latestAnswer}
              onRegenerate={onRegenerate}
            />
          );
        }
        return <StudentMessage key={row.key} message={message} onRetry={onRetry} />;
      })}
      {sending ? <TypingIndicator /> : null}
    </ChatThread>
  );
}
